import { Formik } from 'formik';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import useAuthRecaptchaV2 from '../../Hooks/Recaptcha/useAuthRecaptchaV2';
import useRecaptcha from '../../Hooks/Recaptcha/useRecaptcha';
import useResetPassword from '../../Hooks/fetch/useResetPassword';
import { useTempEdgeTranslator } from '../../Hooks/useTranslation';
import '../../assets/styles/components/AuthForm.css';
import AuthField from '../../components/common/AuthField/AuthField';
import AuthFormShell from '../../components/common/AuthFormShell/AuthFormShell';
import AuthRecaptcha from '../../components/common/AuthRecaptcha/AuthRecaptcha';
import AuthSubmit from '../../components/common/AuthSubmit/AuthSubmit';
import Loader from '../../components/common/Loader/Loader';
import OutcomeBar from '../../components/common/OutcomeBar';
import { validateEmail } from '../../utils/ReGexs';
import './ForgotPassword.css';

const validate = (values) => {
  const errors = {};
  if (!values.user) errors.user = 'required';
  if (!values.email) {
    errors.email = 'required';
  } else if (!validateEmail(values.email)) {
    errors.email = 'invalid';
  }
  return errors;
};

function ForgotPassword() {
  const { tempedgeTranslator, locale } = useTempEdgeTranslator();
  const recaptchaTokenV3 = useRecaptcha('forgot_password');
  const { showRecaptchaV2, isSubmitDisabled, recaptchaV2Verified, handleRecaptchaVerify } = useAuthRecaptchaV2();
  const { submit, loading, result } = useResetPassword();

  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      const data = await submit({ username: values.user, email: values.email, recaptchaToken: recaptchaTokenV3 });
      if (data?.data?.status === 200) {
        resetForm();
      }
    },
    [submit, recaptchaTokenV3]
  );

  if (loading) return <Loader />;

  return (
    <AuthFormShell
      pageClassName="forgot-password-page"
      title={tempedgeTranslator('com.tempedge.msg.title.recoverpassword')}
      subtitle={tempedgeTranslator('com.tempedge.msg.label.recoverpassword.desc')}
    >
      <Formik initialValues={{ user: '', email: '' }} enableReinitialize validate={validate} onSubmit={handleSubmit} autoComplete="nope">
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            {result && <OutcomeBar response={result} />}

            <AuthField
              id="user"
              name="user"
              label={tempedgeTranslator('com.tempedge.msg.label.username')}
              placeholder={tempedgeTranslator('com.tempedge.msg.label.username')}
              minLength={3}
              maxLength={30}
              value={formik.values.user}
              onChange={formik.handleChange}
              hasError={Boolean(formik.errors.user && formik.touched.user)}
            />

            <AuthField
              id="email"
              name="email"
              type="email"
              label={tempedgeTranslator('com.tempedge.msg.label.email')}
              placeholder={tempedgeTranslator('com.tempedge.msg.label.email')}
              value={formik.values.email}
              onChange={(e) => formik.setFieldValue('email', e.target.value.toUpperCase())}
              hasError={Boolean(formik.errors.email && formik.touched.email)}
            />

            <div className="auth-options forgot-password-options">
              <Link to={`/signin/${locale}`} className="auth-link">
                {tempedgeTranslator('com.tempedge.msg.label.sign_in')}
              </Link>
            </div>

            <AuthRecaptcha show={showRecaptchaV2} verified={recaptchaV2Verified} onVerify={handleRecaptchaVerify} />

            <AuthSubmit label={tempedgeTranslator('com.tempedge.msg.label.submit')} disabled={isSubmitDisabled} />
          </form>
        )}
      </Formik>
    </AuthFormShell>
  );
}

export default ForgotPassword;
